import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { uniqBy } from 'lodash';

import Strings from '../../../Strings';
import { DATE_FORMAT_YEAR_MONTH_DAY } from '../../../constants';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { CareProviderFilter, OrgFilter } from '../../../components/AdvancedFilters/FiltersDefinition';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { GET_MY_ORGS_RESULT, actions as orgFilterActions } from '../../../components/OrgSelect/orgFilterActions';
import DatePicker from '../../../components/Inputs/DatePicker';
import { Input } from '../../../containers/Form';
import { TextInput } from '../../../components/PageHeader/TextInput';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { getProgramDisplayName } from '../../../utils/cmsPrograms';
import { actions as organizationActions } from '../../Organizations/redux/actions';
import { GET_CARE_PROVIDERS_RESULT } from '../../Organizations/redux/constants';
import { extractErrorMessage, extractExceptionMessage, formatMoney, formatTimestamp, INVOICE_STATUSES } from './helpers';
import {
  GET_DEVICE_INVOICES_INITIAL_RESULT,
  GET_DEVICE_INVOICES_RECURRING_RESULT,
  invoiceActions,
} from './redux/actions';
import { getShowOnlyMyBillingOrgsPreference, setShowOnlyMyBillingOrgsPreference } from './deviceInvoicePreferences';
import './InvoiceData.scss';

const SEARCH_KEYS = [
  'invoice_type',
  'item_label',
  'billing_org_name',
  'provider_name',
  'invoice_date',
];

const extractInvoiceRows = response => {
  if (Array.isArray(response)) {
    return response;
  }
  return Array.isArray(response?.data) ? response.data : [];
};

const formatInvoiceType = invoiceType => (
  invoiceType === 'recurring'
    ? Strings.deviceInvoices.recurring
    : Strings.deviceInvoices.initial
);

const getInitialItemLabel = row => {
  const label = [row.device_manufacturer, row.device_model].filter(Boolean).join(' ').trim();
  return label || row.device_type || Strings.deviceInvoices.initial;
};

const getRecurringItemLabel = row => {
  if (row.program) {
    return getProgramDisplayName(row.program, row.subprogram || row.program);
  }
  return 'Kit without program';
};

const normalizeInitialRows = rows => (
  rows
    .filter(row => row.invoice_date)
    .map(row => ({
      id: `initial-${row.invoice_uuid}`,
      invoice_date: row.invoice_date,
      invoice_type: formatInvoiceType('initial'),
      invoice_type_key: 'initial',
      item_label: getInitialItemLabel(row),
      billing_org_id: row.billing_org_id || row.customer_id,
      billing_org_name: row.billing_org_name || row.customer_name,
      provider_id: row.provider_id,
      provider_name: row.provider_name,
      price: row.price,
      kitting_cost: row.kitting_fee,
      data_cost: null,
      shipping_cost: row.shipping_fee,
    }))
);

const normalizeRecurringRows = rows => (
  rows
    .filter(row => row.invoice_date)
    .map(row => ({
      id: `recurring-${row.invoice_uuid}`,
      invoice_date: row.invoice_date,
      invoice_type: formatInvoiceType('recurring'),
      invoice_type_key: 'recurring',
      item_label: getRecurringItemLabel(row),
      billing_org_id: row.billing_org_id || row.customer_id,
      billing_org_name: row.billing_org_name || row.customer_name,
      provider_id: row.provider_id,
      provider_name: row.provider_name,
      price: row.price,
      kitting_cost: null,
      data_cost: row.data_fee,
      shipping_cost: null,
    }))
);

const applyBaseFilters = (rows, filters, currentUserBillingOrgs, showOnlyMyBillingOrgs) => {
  let filteredRows = rows;
  const appliedFilters = getRelevantFilters(filters);

  const billingOrgFilters = appliedFilters?.filter(filter => filter.group === 'billing_org_id') || [];
  if (billingOrgFilters.length > 0) {
    filteredRows = filteredRows.filter(row => billingOrgFilters.some(filter => filter.value === row.billing_org_id));
  } else if (showOnlyMyBillingOrgs && currentUserBillingOrgs?.length > 0) {
    filteredRows = filteredRows.filter(row => currentUserBillingOrgs.some(org => org.value === row.billing_org_id));
  }

  const providerFilters = appliedFilters?.filter(filter => filter.group === 'provider') || [];
  if (providerFilters.length > 0) {
    filteredRows = filteredRows.filter(row => providerFilters.some(filter => filter.value === row.provider_id));
  }

  return filteredRows;
};

const applySearch = (rows, searchQuery) => {
  if (!searchQuery) {
    return rows;
  }

  const normalizedQuery = searchQuery.toLowerCase();
  return rows.filter(row =>
    SEARCH_KEYS.some(key => {
      const normalizedValue = row[key]?.toString()?.toLowerCase();
      return normalizedValue ? normalizedValue.includes(normalizedQuery) : false;
    })
  );
};

const aggregateInvoiceRows = rows => {
  const groupedRows = new Map();

  rows.forEach(row => {
    const baseKey = [
      row.invoice_date,
      row.billing_org_id,
      row.provider_id || '',
      row.invoice_type_key,
      row.item_label,
      'item',
    ].join('|');

    const currentBase = groupedRows.get(baseKey) || {
      id: baseKey,
      invoice_date: row.invoice_date,
      billing_org_name: row.billing_org_name,
      provider_name: row.provider_name,
      invoice_type: row.invoice_type,
      item_label: row.item_label,
      quantity: 0,
      price: 0,
      has_price: false,
      shipping_cost: null,
    };

    currentBase.quantity += 1;
    if (row.price != null) {
      currentBase.price += Number(row.price);
      currentBase.has_price = true;
    }
    groupedRows.set(baseKey, currentBase);

    [
      { key: 'kitting', amount: row.kitting_cost, label: Strings.deviceInvoices.kittingFee },
      { key: 'data', amount: row.data_cost, label: Strings.deviceInvoices.dataFee },
    ].forEach(charge => {
      if (charge.amount == null || Number(charge.amount) === 0) {
        return;
      }

      const chargeKey = [
        row.invoice_date,
        row.billing_org_id,
        row.provider_id || '',
        row.invoice_type_key,
        charge.key,
      ].join('|');
      const currentCharge = groupedRows.get(chargeKey) || {
        id: chargeKey,
        invoice_date: row.invoice_date,
        billing_org_name: row.billing_org_name,
        provider_name: row.provider_name,
        invoice_type: row.invoice_type,
        item_label: charge.label,
        quantity: 0,
        price: 0,
        has_price: true,
        shipping_cost: null,
      };
      currentCharge.quantity += 1;
      currentCharge.price += Number(charge.amount);
      groupedRows.set(chargeKey, currentCharge);
    });

    if (row.shipping_cost == null || Number(row.shipping_cost) === 0) {
      return;
    }

    const shippingKey = [
      row.invoice_date,
      row.billing_org_id,
      row.provider_id || '',
      row.invoice_type_key,
      'shipping',
    ].join('|');

    const currentShipping = groupedRows.get(shippingKey) || {
      id: shippingKey,
      invoice_date: row.invoice_date,
      billing_org_name: row.billing_org_name,
      provider_name: row.provider_name,
      invoice_type: row.invoice_type,
      item_label: Strings.deviceInvoices.shippingItemPrefix || 'Shipping',
      quantity: 0,
      price: null,
      shipping_cost: 0,
      shipping_items: new Set(),
    };

    currentShipping.quantity += 1;
    currentShipping.shipping_cost += Number(row.shipping_cost || 0);
    currentShipping.shipping_items.add(row.item_label);
    groupedRows.set(shippingKey, currentShipping);
  });

  return Array.from(groupedRows.values()).map(({ has_price, shipping_items, ...row }) => {
    const shippedDevicesCount = row.quantity || 0;

    return {
      ...row,
      item_label: shipping_items?.size > 0
        ? Strings.formatString(
          Strings.deviceInvoices.shippingItemSummary || 'Shipping ({0} devices)',
          shippedDevicesCount,
        )
        : row.item_label,
      price: has_price ? row.price : null,
    };
  });
};

function InvoiceData(props) {
  const { filters, authConfig } = props;
  const {
    getCareProviders,
    getDeviceInvoiceInitialRows,
    getDeviceInvoiceRecurringRows,
    getOrgs,
    showNotification,
  } = props;

  const [rawRows, setRawRows] = useState({ initial: [], recurring: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [careProviders, setCareProviders] = useState([]);
  const hasBillingOrgScope = Boolean(authConfig?.billing_org_ids?.length);
  const [showOnlyMyBillingOrgs, setShowOnlyMyBillingOrgs] = useState(() => (
    getShowOnlyMyBillingOrgsPreference(Boolean(authConfig?.billing_org_ids?.length))
  ));
  const [selectedInvoiceMonth, setSelectedInvoiceMonth] = useState(() => moment().subtract(1, 'month').startOf('month'));
  const invoiceMonthPickerProps = useMemo(() => ({
    maxDate: moment().endOf('month'),
    openTo: 'month',
    views: ['year', 'month'],
    actions: [],
  }), []);

  useEffect(() => {
    getOrgs().then(resp => {
      if (resp?.type === GET_MY_ORGS_RESULT) {
        setOrgs(
          resp.response?.data?.map(org => ({
            ...org,
            value: org.masked_id,
            label: org.name,
          })) || [],
        );
      }
    });

    getCareProviders().then(resp => {
      if (resp?.type === GET_CARE_PROVIDERS_RESULT) {
        setCareProviders(resp?.response ? uniqBy(resp.response, 'uuid') : []);
      }
    });
  }, [getCareProviders, getOrgs]);

  const currentUserBillingOrgs = useMemo(() => (
    orgs.filter(org => authConfig?.billing_org_ids?.includes(org.masked_id))
  ), [authConfig, orgs]);

  useEffect(() => {
    if (!hasBillingOrgScope && showOnlyMyBillingOrgs) {
      setShowOnlyMyBillingOrgs(false);
    }
  }, [hasBillingOrgScope, showOnlyMyBillingOrgs]);

  useEffect(() => {
    setShowOnlyMyBillingOrgsPreference(hasBillingOrgScope && showOnlyMyBillingOrgs);
  }, [hasBillingOrgScope, showOnlyMyBillingOrgs]);

  const billingOrgsForFilter = useMemo(() => {
    if (hasBillingOrgScope && showOnlyMyBillingOrgs) {
      return currentUserBillingOrgs;
    }
    return orgs;
  }, [currentUserBillingOrgs, hasBillingOrgScope, orgs, showOnlyMyBillingOrgs]);

  useEffect(() => {
    setIsLoading(true);
    const invoiceMonth = selectedInvoiceMonth.format('YYYY-MM');

    Promise.all([
      getDeviceInvoiceInitialRows({ status: INVOICE_STATUSES.approved.key, invoiceMonth }),
      getDeviceInvoiceRecurringRows({ status: INVOICE_STATUSES.approved.key, invoiceMonth }),
    ])
      .then(([initialResp, recurringResp]) => {
        if (initialResp?.type !== GET_DEVICE_INVOICES_INITIAL_RESULT) {
          showNotification(extractErrorMessage(initialResp), 5000, true);
          return;
        }
        if (recurringResp?.type !== GET_DEVICE_INVOICES_RECURRING_RESULT) {
          showNotification(extractErrorMessage(recurringResp), 5000, true);
          return;
        }

        setRawRows({
          initial: extractInvoiceRows(initialResp.response),
          recurring: extractInvoiceRows(recurringResp.response),
        });
      })
      .catch(error => {
        showNotification(extractExceptionMessage(error), 5000, true);
      })
      .finally(() => setIsLoading(false));
  }, [getDeviceInvoiceInitialRows, getDeviceInvoiceRecurringRows, selectedInvoiceMonth, showNotification]);

  const aggregatedRows = useMemo(() => {
    const normalizedRows = normalizeInitialRows(rawRows.initial).concat(normalizeRecurringRows(rawRows.recurring));
    const filteredRows = applyBaseFilters(
      normalizedRows,
      filters,
      currentUserBillingOrgs,
      showOnlyMyBillingOrgs,
    );
    return applySearch(aggregateInvoiceRows(filteredRows), searchQuery);
  }, [currentUserBillingOrgs, filters, rawRows, searchQuery, showOnlyMyBillingOrgs]);

  const aggregatedColumns = useMemo(() => ([
    columnHelper.accessor('invoice_date', {
      header: Strings.deviceInvoices.invoiceDate,
      cell: info => formatTimestamp(info.getValue(), DATE_FORMAT_YEAR_MONTH_DAY),
      sortingFn: 'dateSortingFn',
      size: 140,
    }),
    columnHelper.accessor('billing_org_name', {
      header: Strings.deviceInvoices.billingOrg || 'Billing Org',
      cell: info => info.getValue() || '-',
      size: 220,
    }),
    columnHelper.accessor('provider_name', {
      header: Strings.capPatient.provider || 'Provider',
      cell: info => info.getValue() || '-',
      size: 220,
    }),
    columnHelper.accessor('invoice_type', {
      header: Strings.type,
      cell: info => info.getValue() || '-',
      size: 120,
    }),
    columnHelper.accessor('item_label', {
      header: Strings.deviceInvoices.item || 'Item',
      cell: info => info.getValue() || '-',
      size: 260,
    }),
    columnHelper.accessor('quantity', {
      header: Strings.quantity,
      cell: info => info.getValue(),
      size: 100,
    }),
    columnHelper.accessor('price', {
      header: Strings.deviceInvoices.price,
      cell: info => formatMoney(info.getValue()),
      size: 120,
    }),
    columnHelper.accessor('shipping_cost', {
      header: Strings.shipping.shippingCost,
      cell: info => formatMoney(info.getValue()),
      size: 140,
    }),
  ]), []);

  const customFilters = useMemo(() => ({
    ...OrgFilter(billingOrgsForFilter, false, 'billing_org_id', Strings.deviceInvoices.billingOrg || 'Billing Org'),
    ...CareProviderFilter(careProviders),
  }), [billingOrgsForFilter, careProviders]);

  const leftContent = useMemo(() => (
    <div className="invoice-data-month-selector">
      <span className="invoice-data-month-selector-label">
        {Strings.deviceInvoices.invoiceDate}
      </span>
      <DatePicker
        id="invoice-data-month-selector"
        value={selectedInvoiceMonth}
        onChange={value => {
          if (value) {
            setSelectedInvoiceMonth(value.clone().startOf('month'));
          }
        }}
        openTo="month"
        views={['year', 'month']}
        format="MM/YYYY"
        hideClearButton
        maxDate={invoiceMonthPickerProps.maxDate}
        actions={invoiceMonthPickerProps.actions}
      />
    </div>
  ), [invoiceMonthPickerProps.actions, invoiceMonthPickerProps.maxDate, selectedInvoiceMonth]);

  const rightContent = useMemo(() => (
    <>
      {hasBillingOrgScope && (
        <Input
          type="checkbox"
          id="show-only-my-billing-orgs-invoice-data"
          checked={showOnlyMyBillingOrgs}
          onChange={e => setShowOnlyMyBillingOrgs(e.target.checked)}
          label="Show only my billing orgs"
          rowClassName="checkbox-row"
        />
      )}
      <TextInput placeholder={Strings.search} onChange={e => setSearchQuery(e.target.value)} />
      <AdvancedFilters>
        {billingOrgsForFilter?.length > 0 && (
          <Filter definition={OrgFilter(billingOrgsForFilter, false, 'billing_org_id', Strings.deviceInvoices.billingOrg || 'Billing Org').billing_org_id} />
        )}
        {careProviders?.length > 0 && <Filter definition={CareProviderFilter(careProviders).provider} />}
      </AdvancedFilters>
    </>
  ), [billingOrgsForFilter, careProviders, hasBillingOrgScope, showOnlyMyBillingOrgs]);

  return (
    <>
      <div className="tab-buttons-row">
        {leftContent}
        <div className="tab-buttons-right">
          {rightContent}
        </div>
      </div>
      <AdvancedFiltersBar customFilters={customFilters} />
      <Table3
        isLoading={isLoading}
        name="device-invoices-invoice-data-aggregated"
        data={aggregatedRows}
        columns={aggregatedColumns}
        sorting={[
          { id: 'invoice_date', desc: true },
          { id: 'billing_org_name', desc: false },
          { id: 'invoice_type', desc: false },
          { id: 'item_label', desc: false },
        ]}
        enableColumnFiltering
      />
    </>
  );
}

InvoiceData.propTypes = {
  authConfig: PropTypes.shape({
    billing_org_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  filters: PropTypes.array.isRequired,
  getCareProviders: PropTypes.func.isRequired,
  getDeviceInvoiceInitialRows: PropTypes.func.isRequired,
  getDeviceInvoiceRecurringRows: PropTypes.func.isRequired,
  getOrgs: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getDeviceInvoiceInitialRows: params => dispatch(invoiceActions.getDeviceInvoicesInitial(params)),
  getDeviceInvoiceRecurringRows: params => dispatch(invoiceActions.getDeviceInvoicesRecurring(params)),
  getOrgs: request => dispatch(orgFilterActions.getMyOrgs(request)),
  getCareProviders: request => dispatch(organizationActions.getCareProviders(request)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

const mapStateToProps = state => ({
  authConfig: state.auth.config,
  filters: state.entities.advancedFilters.filters.items,
});

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceData);
